/**
 * Bill Service — Budget Management (011)
 */

import { supabase, getCurrentUserId } from '../lib/supabase';
import {
  Bill,
  BillSummary,
  QuickReminder,
  BillReminderPayload,
  CreateBillInput,
  UpdateBillInput,
  BillServiceResponse,
  getBillDisplayName,
} from '../types/bill.types';
import {
  getBillRelativeTiming,
  getBillDaysUntilDue,
  computeBillStatus,
  getEffectiveDueDate,
  getLatestOccurrenceOnOrBefore,
  advanceDueDate,
  manualBillNeedsAttention,
  autopayBillDueSoon,
  isAutopayPastGrace,
  formatBillReminderMessage,
  getAutopayReviewMessage,
  AUTOPAY_GRACE_PERIOD_DAYS,
  BILL_DUE_SOON_DAYS,
} from '../utils/budget-calculations';
import { validateCreateBill } from '../utils/bill-validation';
import { notificationService } from './notification-service';

const BILL_CATEGORY_SELECT = 'categories(name, color, parent_id)';
const BILL_SELECT = `*, ${BILL_CATEGORY_SELECT}`;

type CategoryJoinRow = {
  name?: string;
  color?: string | null;
  parent_id?: string | null;
};

type ParentCategoryRow = { name: string; color: string | null };

type BillPaymentMatchInput = {
  categoryId?: string | null;
  amount: number;
  dueDate: string;
  linkedTransactionId?: string | null;
};

type BillRow = {
  id: string;
  user_id: string;
  name: string;
  display_name?: string | null;
  source?: string;
  category_id?: string | null;
  categories?: CategoryJoinRow | null;
  amount: unknown;
  frequency: string;
  due_date: string;
  day_of_month?: number | null;
  autopay: boolean;
  status: string;
  linked_transaction_id?: string | null;
  last_paid_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayLocalIso(today: Date = new Date()): string {
  return formatLocalDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
}

async function loadParentCategoryMap(
  rows: Array<{ categories?: CategoryJoinRow | null }>
): Promise<Map<string, ParentCategoryRow>> {
  const parentIds = [
    ...new Set(
      rows
        .map((row) => row.categories?.parent_id)
        .filter((id): id is string => !!id)
    ),
  ];
  if (parentIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from('categories')
    .select('id, name, color')
    .in('id', parentIds);

  if (error || !data) return new Map();
  return new Map(data.map((row) => [row.id, { name: row.name, color: row.color }]));
}

function mapPrimaryCategory(
  category: CategoryJoinRow | null | undefined,
  parentMap: Map<string, ParentCategoryRow>
): Pick<Bill, 'categoryName' | 'primaryCategoryName' | 'primaryCategoryColor'> {
  if (!category?.name) return {};
  const parent = category.parent_id ? parentMap.get(category.parent_id) : undefined;
  const primary = parent ?? category;
  return {
    categoryName: category.name,
    primaryCategoryName: primary.name,
    primaryCategoryColor: (primary.color ?? category.color) ?? undefined,
  };
}

function paymentWindowForBill(dueDate: string): { from: string; to: string } {
  const [y, m, d] = dueDate.split('-').map(Number);
  const due = new Date(y, (m ?? 1) - 1, d ?? 1);
  const from = new Date(due);
  from.setDate(from.getDate() - 7);
  const to = new Date(due);
  to.setDate(to.getDate() + AUTOPAY_GRACE_PERIOD_DAYS + 7);
  return {
    from: formatLocalDate(from),
    to: formatLocalDate(to),
  };
}

function isDateInPaymentWindow(dateIso: string, dueDate: string): boolean {
  const day = dateIso.slice(0, 10);
  const { from, to } = paymentWindowForBill(dueDate);
  return day >= from && day <= to;
}

function findMatchingTransactionInRows(
  txns: Array<{ id: string; amount: unknown }>,
  amount: number
): string | null {
  const match = txns.find((t) => Math.abs(parseFloat(String(t.amount))) === amount);
  return match?.id ?? null;
}

async function findMatchingBillTransaction(
  userId: string,
  bill: BillPaymentMatchInput
): Promise<string | null> {
  if (bill.linkedTransactionId) {
    const { data: linked } = await supabase
      .from('transactions')
      .select('id, date')
      .eq('id', bill.linkedTransactionId)
      .maybeSingle();
    if (linked?.date && isDateInPaymentWindow(String(linked.date), bill.dueDate)) {
      return linked.id;
    }
  }
  if (!bill.categoryId) return null;

  const { from, to } = paymentWindowForBill(bill.dueDate);
  const { data: txns } = await supabase
    .from('transactions')
    .select('id, amount')
    .eq('user_id', userId)
    .eq('category_id', bill.categoryId)
    .gte('date', from)
    .lte('date', to)
    .is('archived_at', null);

  return findMatchingTransactionInRows(txns ?? [], bill.amount);
}

function resolveEffectiveDue(
  row: Pick<BillRow, 'due_date' | 'frequency' | 'day_of_month' | 'last_paid_at' | 'status'>,
  today: Date = new Date()
): { effectiveDue: string; status: Bill['status'] } {
  if (row.frequency === 'one_off' && row.status === 'paid') {
    return { effectiveDue: row.due_date, status: 'paid' };
  }

  const effectiveDue = getEffectiveDueDate(
    {
      dueDate: row.due_date,
      frequency: row.frequency,
      dayOfMonth: row.day_of_month,
      lastPaidAt: row.last_paid_at,
    },
    today
  );

  return {
    effectiveDue,
    status: computeBillStatus(effectiveDue, false, today),
  };
}

function billStatusTag(args: {
  status: Bill['status'];
  autopay: boolean;
  dueDate: string;
}): string {
  if (args.status === 'paid') return 'Paid';
  if (
    args.autopay &&
    getBillDaysUntilDue(args.dueDate) < 0 &&
    !isAutopayPastGrace(args.dueDate)
  ) {
    return 'Autopay pending';
  }
  return args.status.charAt(0).toUpperCase() + args.status.slice(1);
}

async function mapBillRows(rows: BillRow[]): Promise<Bill[]> {
  const parentMap = await loadParentCategoryMap(rows);
  return rows.map((row) => mapBill(row, parentMap));
}

async function mapBillRow(row: BillRow): Promise<Bill> {
  const parentMap = await loadParentCategoryMap([row]);
  return mapBill(row, parentMap);
}

function mapBill(row: BillRow, parentMap: Map<string, ParentCategoryRow> = new Map()): Bill {
  const today = new Date();
  const { effectiveDue, status } = resolveEffectiveDue(row, today);
  const tags: string[] = [billStatusTag({ status, autopay: row.autopay, dueDate: effectiveDue })];
  if (row.autopay) tags.push('Auto-pay');
  const name = row.name;
  return {
    id: row.id,
    userId: row.user_id,
    name,
    displayName: (row.display_name as string | null | undefined)?.trim() || name,
    source: row.source === 'scan' ? 'scan' : 'manual',
    categoryId: row.category_id ?? undefined,
    ...mapPrimaryCategory(row.categories, parentMap),
    amount: parseFloat(String(row.amount)),
    frequency: row.frequency as Bill['frequency'],
    anchorDate: row.due_date,
    dueDate: effectiveDue,
    dayOfMonth: row.day_of_month ?? undefined,
    autopay: row.autopay,
    status,
    statusTags: tags,
    relativeTiming: getBillRelativeTiming(effectiveDue, status, today, row.autopay),
    linkedTransactionId: row.linked_transaction_id ?? undefined,
    lastPaidAt: row.last_paid_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function resolveBillReminders(userId: string, bills: Bill[]): Promise<BillReminderPayload[]> {
  const reminders: BillReminderPayload[] = [];

  for (const bill of bills) {
    if (bill.status === 'paid') continue;
    const label = getBillDisplayName(bill);

    if (!bill.autopay) {
      if (!manualBillNeedsAttention(bill.dueDate, bill.status, bill.autopay)) continue;
      const timing = getBillRelativeTiming(bill.dueDate, bill.status, new Date(), false);
      const isOverdue = getBillDaysUntilDue(bill.dueDate) < 0;
      reminders.push({
        id: bill.id,
        name: label,
        amount: bill.amount,
        dueDate: bill.dueDate,
        message: formatBillReminderMessage(label, bill.amount, timing),
        severity: isOverdue ? 'critical' : 'warning',
        autopay: false,
      });
      continue;
    }

    if (isAutopayPastGrace(bill.dueDate)) {
      const hasPayment = await findMatchingBillTransaction(userId, {
        categoryId: bill.categoryId ?? null,
        amount: bill.amount,
        dueDate: bill.dueDate,
        linkedTransactionId: bill.linkedTransactionId ?? null,
      });
      if (!hasPayment) {
        reminders.push({
          id: bill.id,
          name: label,
          amount: bill.amount,
          dueDate: bill.dueDate,
          message: getAutopayReviewMessage(label, bill.amount),
          severity: 'warning',
          autopay: true,
        });
      }
      continue;
    }

    if (autopayBillDueSoon(bill.dueDate, bill.status)) {
      const timing = getBillRelativeTiming(bill.dueDate, bill.status, new Date(), true);
      reminders.push({
        id: bill.id,
        name: label,
        amount: bill.amount,
        dueDate: bill.dueDate,
        message: formatBillReminderMessage(label, bill.amount, timing),
        severity: 'info',
        autopay: true,
      });
    }
  }

  return reminders;
}

class BillService {
  async getSummary(userId?: string): Promise<BillServiceResponse<BillSummary>> {
    const list = await this.listBills(undefined, userId);
    if (list.error) return { error: list.error };
    const bills = list.data ?? [];
    const totalMonthly = bills
      .filter((b) => b.frequency === 'monthly' && b.status !== 'paid')
      .reduce((s, b) => s + b.amount, 0);
    const billsDueSoonCount = bills.filter((b) => {
      if (b.status === 'paid') return false;
      const days = getBillDaysUntilDue(b.dueDate);
      return days >= 0 && days <= BILL_DUE_SOON_DAYS;
    }).length;
    const autopay = bills.filter((b) => b.autopay).length;
    return {
      data: {
        totalMonthlyBills: totalMonthly,
        billsDueSoonCount,
        billsOnAutopayCount: autopay,
      },
    };
  }

  async listBills(filter?: string, userId?: string): Promise<BillServiceResponse<Bill[]>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { data, error } = await supabase
        .from('bills')
        .select(BILL_SELECT)
        .eq('user_id', uid)
        .order('due_date', { ascending: true });

      if (error) return { error: { code: 'DB_ERROR', message: error.message } };

      await this.reconcileBillPayments(uid, (data ?? []) as BillRow[]);
      await this.recomputeBillStatuses(uid);

      let refreshQuery = supabase
        .from('bills')
        .select(BILL_SELECT)
        .eq('user_id', uid)
        .order('due_date', { ascending: true });
      if (filter && filter !== 'all') {
        refreshQuery = refreshQuery.eq('status', filter);
      }
      const { data: refreshed, error: refreshError } = await refreshQuery;
      if (refreshError) return { error: { code: 'DB_ERROR', message: refreshError.message } };

      const bills = await mapBillRows((refreshed ?? []) as BillRow[]);
      bills.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
      const reminders = await resolveBillReminders(uid, bills);
      await notificationService.syncBillReminders(uid, reminders);
      return { data: bills };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async createBill(input: CreateBillInput, userId?: string): Promise<BillServiceResponse<Bill>> {
    const parsed = validateCreateBill(input);
    if (!parsed.success) {
      return { error: { code: 'VALIDATION_ERROR', message: parsed.error.issues[0]?.message ?? 'Invalid' } };
    }
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const effective = getEffectiveDueDate({
        dueDate: input.dueDate,
        frequency: input.frequency,
        dayOfMonth: input.dayOfMonth,
      });
      const status = computeBillStatus(effective, false);
      const displayName = (input.displayName?.trim() || input.name).trim();
      const { data, error } = await supabase
        .from('bills')
        .insert({
          user_id: uid,
          name: input.name.trim(),
          display_name: displayName,
          source: input.source ?? 'manual',
          category_id: input.categoryId ?? null,
          amount: input.amount,
          frequency: input.frequency,
          due_date: input.dueDate,
          day_of_month: input.dayOfMonth ?? null,
          autopay: input.autopay ?? false,
          status,
        })
        .select(BILL_SELECT)
        .single();

      if (error || !data) return { error: { code: 'DB_ERROR', message: error?.message ?? 'Failed' } };
      return { data: await mapBillRow(data as BillRow) };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async updateBill(id: string, input: UpdateBillInput, userId?: string): Promise<BillServiceResponse<Bill>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };

      const { data: existing, error: existingErr } = await supabase
        .from('bills')
        .select('source')
        .eq('id', id)
        .eq('user_id', uid)
        .single();
      if (existingErr || !existing) {
        return { error: { code: 'NOT_FOUND', message: existingErr?.message ?? 'Not found' } };
      }

      if (input.name && existing.source !== 'scan') {
        updates.name = input.name.trim();
      }
      if (input.displayName !== undefined) {
        const dn = input.displayName.trim();
        if (dn) updates.display_name = dn;
      }
      if (input.categoryId !== undefined) updates.category_id = input.categoryId;
      if (input.amount) updates.amount = input.amount;
      if (input.frequency) updates.frequency = input.frequency;
      if (input.dueDate) updates.due_date = input.dueDate;
      if (input.dayOfMonth !== undefined) updates.day_of_month = input.dayOfMonth;
      if (input.autopay !== undefined) updates.autopay = input.autopay;

      const { data, error } = await supabase
        .from('bills')
        .update(updates)
        .eq('id', id)
        .eq('user_id', uid)
        .select(BILL_SELECT)
        .single();

      if (error || !data) return { error: { code: 'NOT_FOUND', message: error?.message ?? 'Not found' } };
      return { data: await mapBillRow(data as BillRow) };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async deleteBill(id: string, userId?: string): Promise<BillServiceResponse<{ deleted: true }>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { error } = await supabase.from('bills').delete().eq('id', id).eq('user_id', uid);
      if (error) return { error: { code: 'DB_ERROR', message: error.message } };
      return { data: { deleted: true } };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async markPaid(id: string, userId?: string): Promise<BillServiceResponse<Bill>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { data: bill, error: fetchErr } = await supabase
        .from('bills')
        .select('*')
        .eq('id', id)
        .eq('user_id', uid)
        .single();

      if (fetchErr || !bill) return { error: { code: 'NOT_FOUND', message: 'Bill not found' } };

      const today = new Date();
      const todayStr = todayLocalIso(today);
      const cycleDue =
        bill.frequency === 'one_off'
          ? bill.due_date
          : getLatestOccurrenceOnOrBefore(
              bill.due_date,
              bill.frequency,
              bill.day_of_month,
              todayStr
            ) ??
            getEffectiveDueDate(
              {
                dueDate: bill.due_date,
                frequency: bill.frequency,
                dayOfMonth: bill.day_of_month,
              },
              today
            );

      const linkedId = await findMatchingBillTransaction(uid, {
        categoryId: bill.category_id,
        amount: parseFloat(String(bill.amount)),
        dueDate: cycleDue,
        linkedTransactionId: bill.linked_transaction_id,
      });

      const updates: Record<string, unknown> = {
        linked_transaction_id: linkedId,
        last_paid_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (bill.frequency === 'one_off') {
        updates.status = 'paid';
      } else {
        const nextDue = advanceDueDate(cycleDue, bill.frequency, bill.day_of_month);
        updates.status = computeBillStatus(nextDue, false, today);
      }

      const { data, error } = await supabase
        .from('bills')
        .update(updates)
        .eq('id', id)
        .select(BILL_SELECT)
        .single();

      if (error || !data) return { error: { code: 'DB_ERROR', message: error.message } };
      return { data: await mapBillRow(data as BillRow) };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async getQuickReminders(userId?: string): Promise<BillServiceResponse<QuickReminder[]>> {
    const list = await this.listBills(undefined, userId);
    if (list.error) return { error: list.error };
    const uid = userId ?? (await getCurrentUserId());
    if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

    const reminders = await resolveBillReminders(uid, list.data ?? []);
    return {
      data: reminders.map((reminder) => ({
        billId: reminder.id,
        name: reminder.name,
        amount: reminder.amount,
        dueDate: reminder.dueDate,
        severity: reminder.severity === 'critical' ? 'critical' : 'warning',
        message: reminder.message,
        primaryAction: reminder.autopay ? 'review' : 'pay_now',
        autopay: reminder.autopay,
      })),
    };
  }

  async reconcileBillPayments(userId: string, bills: BillRow[]): Promise<void> {
    const today = new Date();
    const todayStr = todayLocalIso(today);

    for (const bill of bills) {
      if (bill.frequency === 'one_off' || bill.status === 'paid') continue;

      const cycleDue = getLatestOccurrenceOnOrBefore(
        bill.due_date,
        bill.frequency,
        bill.day_of_month,
        todayStr
      );
      if (!cycleDue) continue;

      if (bill.last_paid_at && isDateInPaymentWindow(bill.last_paid_at, cycleDue)) {
        continue;
      }

      const linkedId = await findMatchingBillTransaction(userId, {
        categoryId: bill.category_id,
        amount: parseFloat(String(bill.amount)),
        dueDate: cycleDue,
        linkedTransactionId: null,
      });
      if (!linkedId) continue;

      const nextDue = advanceDueDate(cycleDue, bill.frequency, bill.day_of_month);
      const status = computeBillStatus(nextDue, false, today);

      await supabase
        .from('bills')
        .update({
          linked_transaction_id: linkedId,
          last_paid_at: new Date().toISOString(),
          status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', bill.id)
        .eq('user_id', userId);
    }
  }

  async recomputeBillStatuses(userId: string): Promise<void> {
    const { data: bills } = await supabase.from('bills').select('*').eq('user_id', userId);
    for (const bill of (bills ?? []) as BillRow[]) {
      if (bill.frequency === 'one_off' && bill.status === 'paid') continue;
      const { status } = resolveEffectiveDue(bill);
      if (status !== bill.status) {
        await supabase
          .from('bills')
          .update({ status, updated_at: new Date().toISOString() })
          .eq('id', bill.id);
      }
    }
  }
}

export const billService = new BillService();

export async function getBillSummary(userId?: string) {
  return billService.getSummary(userId);
}
export async function listBills(filter?: string, userId?: string) {
  return billService.listBills(filter, userId);
}
export async function createBill(input: CreateBillInput, userId?: string) {
  return billService.createBill(input, userId);
}
export async function updateBill(id: string, input: UpdateBillInput, userId?: string) {
  return billService.updateBill(id, input, userId);
}
export async function deleteBill(id: string, userId?: string) {
  return billService.deleteBill(id, userId);
}
export async function markPaid(id: string, userId?: string) {
  return billService.markPaid(id, userId);
}
export async function getQuickReminders(userId?: string) {
  return billService.getQuickReminders(userId);
}
